/**
 * Тосты о карточках, которые главная не показала (ТЗ главной § 8, «Без двойных уведомлений»): пока ученик на
 * главной, итог нажатия виден в записи «Недавно добавленных» и в сигнале у строки. Если он ушёл с главной, не
 * приняв итог (сигнал не нажат и не убран, строка не была на экране, «Готово» не досидело свои 6 с), — эти
 * тосты показываются при уходе: сбой не теряется. Ошибки — каждая своим тостом, как обычно; успехи — одним
 * итогом. На каждую карточку — последнее, что о ней пришло. Чистая модель без alias-импортов.
 */

export interface DeferredSuccess<T> {
  cardId: string;
  front: string;
  /** Тост одиночной карточки, если он был («Карточка «travel» готова»). */
  toast?: T;
}

export type DeferredToast<T> = { kind: 'failure'; toast: T } | { kind: 'success'; front: string; toast?: T };

export type DeferredToasts<T> = Map<string, DeferredToast<T>>;

export interface ReleasedToasts<T> {
  failures: T[];
  successes: DeferredSuccess<T>[];
  /** Карточки, о которых что-то показано: их записи помечаются `announced`. */
  cardIds: string[];
}

/** Отложить тост карточки: прежний о той же карточке (до «Повторить») заменяется. */
export function deferToast<T>(stash: DeferredToasts<T>, cardId: string, entry: DeferredToast<T>): void {
  stash.delete(cardId);
  stash.set(cardId, entry);
}

/**
 * Уход с главной: из отложенного — сбои карточек `failureCardIds` и успехи карточек `successCardIds` (нажатия
 * с непринятым итогом, см. `unacceptedOutcomes`), в порядке прихода. Остальное отбрасывается: итог принят
 * или это прошлый заход — запись покажет его сама.
 */
export function releaseDeferred<T>(
  stash: ReadonlyMap<string, DeferredToast<T>>,
  failureCardIds: Iterable<string>,
  successCardIds: Iterable<string>,
): ReleasedToasts<T> {
  const failing = new Set(failureCardIds);
  const succeeding = new Set(successCardIds);
  const out: ReleasedToasts<T> = { failures: [], successes: [], cardIds: [] };
  for (const [cardId, entry] of stash) {
    if (entry.kind === 'failure' && failing.has(cardId)) {
      out.failures.push(entry.toast);
      out.cardIds.push(cardId);
    } else if (entry.kind === 'success' && succeeding.has(cardId)) {
      out.successes.push({ cardId, front: entry.front, toast: entry.toast });
      out.cardIds.push(cardId);
    }
  }
  return out;
}
